import React, { useEffect, useMemo, useCallback, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { Button, Space, Typography, Modal, Skeleton, Dropdown } from "antd";
import {
  TeamOutlined,
  InfoCircleOutlined,
  ReloadOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  ExportOutlined,
} from "@ant-design/icons";
import GroupSearchModal from "./GroupSearchModal";
import { getGroupById } from "../../actions";
import { sidebar_groups } from "../../config";
import { ContentTable } from "@authzsvc/common-react-components";

const { Text, Link } = Typography;

const AdminGroup = ({ id, input, getGroupById }) => {
  const isEditable = typeof input?.onChange === "function";
  const groupId = isEditable ? (input?.value ?? null) : (id ?? null);
  const [selectedGroup, setSelectedGroup] = useState(undefined);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [loadingGroup, setLoadingGroup] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!groupId) {
        if (mounted) setSelectedGroup(undefined);
        return;
      }
      setLoadingGroup(true);
      const resp = await getGroupById(String(groupId));
      if (mounted) {
        setSelectedGroup(resp?.payload?.data);
        setLoadingGroup(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [groupId, getGroupById]);

  const onClear = useCallback(() => {
    if (!isEditable) return;
    input.onChange(null);
    setSelectedGroup(undefined);
  }, [isEditable, input]);

  const onSelectGroup = useCallback(
    (value) => {
      if (!isEditable) return;
      if (value?.id != null) {
        input.onChange(value.id);
        setPickerOpen(false);
      }
    },
    [isEditable, input],
  );

  const portalHref = useMemo(() => {
    if (!selectedGroup) return "#";
    return `${sidebar_groups}/group/${selectedGroup.groupIdentifier}/details`;
  }, [selectedGroup]);

  const tableColumns = useMemo(
    () => [
      { title: "Field", dataIndex: "field", key: "field", width: 180 },
      {
        title: "Value",
        dataIndex: "value",
        key: "value",
        searchable: true,
        render: (val) =>
          (val ?? val === 0) ? val : <Text type="secondary">—</Text>,
      },
    ],
    [],
  );

  const tableData = useMemo(() => {
    if (!selectedGroup) return [];
    return [
      {
        key: "identifier",
        field: "Identifier",
        value: <Text code>{selectedGroup.groupIdentifier}</Text>,
      },
      {
        key: "displayName",
        field: "Display Name",
        value: selectedGroup.displayName,
      },
      {
        key: "description",
        field: "Description",
        value: selectedGroup.description,
      },
      {
        key: "blocked",
        field: "Blocked",
        value: selectedGroup.blocked ? "true" : "false",
      },
    ];
  }, [selectedGroup]);

  const menuItems = useMemo(() => {
    const items = [];
    if (isEditable) {
      items.push(
        {
          key: "change",
          icon: <ReloadOutlined style={{ color: "#1677ff" }} />,
          label: <span style={{ color: "#1677ff" }}>Change</span>,
          style: { backgroundColor: "#e6f4ff" },
        },
        { type: "divider" },
      );
    }
    items.push(
      { key: "details", icon: <InfoCircleOutlined />, label: "Details" },
      { type: "divider" },
      {
        key: "portal",
        icon: <ExportOutlined />,
        label: (
          <a href={portalHref} target="_blank" rel="noopener noreferrer">
            Groups Portal
          </a>
        ),
      },
    );
    if (isEditable) {
      items.push(
        { type: "divider" },
        {
          key: "remove",
          danger: true,
          icon: <DeleteOutlined />,
          label: "Remove",
        },
      );
    }
    return items;
  }, [isEditable, portalHref]);

  const primaryLabel = loadingGroup
    ? "Loading..."
    : selectedGroup?.displayName ||
      (isEditable ? "Select Admin Group" : "None");

  return (
    <Space direction="vertical" style={{ width: "100%" }}>
      <Space wrap>
        {!selectedGroup ? (
          <Button
            id="adminGroupButton"
            color="gray"
            variant="outlined"
            icon={<TeamOutlined />}
            onClick={() => isEditable && setPickerOpen(true)}
            disabled={!isEditable}
          >
            {primaryLabel}
          </Button>
        ) : (
          <Dropdown
            trigger={["click"]}
            placement="bottomLeft"
            disabled={!isEditable}
            menu={{
              items: menuItems,
              onClick: ({ key }) => {
                if (key === "change") setPickerOpen(true);
                if (key === "details") setDetailsOpen(true);
                if (key === "remove") setRemoveOpen(true);
                // "portal" handled by anchor
              },
            }}
          >
            <Button
              id="adminGroupButton"
              color="gray"
              variant="outlined"
              icon={<TeamOutlined />}
            >
              {primaryLabel}
            </Button>
          </Dropdown>
        )}

        {loadingGroup && (
          <Skeleton
            active
            paragraph={{ rows: 0 }}
            title={{ width: 160 }}
            style={{ marginLeft: 8 }}
          />
        )}
      </Space>

      {/* Only enabled in edit mode */}
      <GroupSearchModal
        title={
          selectedGroup
            ? "Change Administrator Group"
            : "Select Administrator Group"
        }
        onSelect={onSelectGroup}
        open={isEditable && pickerOpen}
        onOpenChange={setPickerOpen}
      />

      <Modal
        open={detailsOpen}
        onCancel={() => setDetailsOpen(false)}
        onOk={() => setDetailsOpen(false)}
        okButtonProps={{ style: { display: "none" } }}
        cancelButtonProps={{ style: { display: "none" } }}
        title={
          <Space>
            <TeamOutlined />
            <span>Group: {selectedGroup?.displayName || "Group details"}</span>
          </Space>
        }
        centered
        closable
        width={600}
      >
        <ContentTable
          loading={false}
          columns={tableColumns}
          dataSource={tableData}
          pagination={false}
          rowKey="key"
        />
        <Space
          style={{ marginTop: 16, display: "flex", justifyContent: "flex-end" }}
        >
          <Link target="_blank" rel="noopener noreferrer" href={portalHref}>
            Open in Groups Portal <ExportOutlined />
          </Link>
        </Space>
      </Modal>

      <Modal
        open={removeOpen}
        onCancel={() => setRemoveOpen(false)}
        onOk={() => {
          onClear();
          setRemoveOpen(false);
        }}
        okText="Remove"
        cancelText="Cancel"
        okButtonProps={{ danger: true, type: "primary" }}
        centered
        keyboard
        width={520}
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <ExclamationCircleOutlined
              style={{ fontSize: 24, color: "#faad14" }}
            />
            <span>Remove Administrator Group</span>
          </div>
        }
        closable
      >
        <Text>
          Are you sure you want to remove the selected administrator group
          {selectedGroup ? (
            <>
              {" "}
              <Text strong>{selectedGroup.displayName}</Text>?
            </>
          ) : (
            "?"
          )}{" "}
          This will clear the selection.
        </Text>
      </Modal>
    </Space>
  );
};

AdminGroup.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  input: PropTypes.shape({
    value: PropTypes.any,
    onChange: PropTypes.func,
  }),
  getGroupById: PropTypes.func.isRequired,
};

export default connect(null, { getGroupById })(AdminGroup);
